import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, useParams, useNavigate } from 'react-router-dom'
import type { State } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  deleteTrack,
  deleteTrackFormat,
} from '@disco/state-management/actions/ServerActions'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { canFormatBeOptimised } from '@disco/utils/file-utils'
import DocumentHeadTags from 'components/document-head-tags'
import ModalDialog from 'components/ui/modal-dialog'
import type { RootState } from 'store/configureStore'
interface OwnProps {
  params: {
    trackId: string
    format: string
  }
  navigate: NavigateFunction
}

function mapStateToProps(state: RootState, props: OwnProps) {
  return {
    track: getTrack(state, {
      id: parseInt(props.params.trackId, 10),
    }),
  }
}

const mapDispatchToProps = {
  deleteTrack,
  deleteTrackFormat,
  sendMixpanelEvent,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>
type DeleteTrackFormatProps = PropsFromRedux & OwnProps

class DeleteTrackFormat extends Component<DeleteTrackFormatProps> {
  _hasOnlyOneFormat() {
    const { track } = this.props
    return Object.keys(track.all_files || {}).length === 1
  }

  _onClose = () =>
    this.props.navigate(`/explorer/tracks/${this.props.track.id}/formats`)
  _onDelete = () => {
    if (this._hasOnlyOneFormat()) {
      this.props.deleteTrack(this.props.track)
      this.props.navigate('/')
    } else {
      const event = canFormatBeOptimised(this.props.params.format)
        ? 'Delete lossless version'
        : 'Delete optimised version'
      this.props.sendMixpanelEvent(event, {
        Format: this.props.params.format,
      })
      this.props.deleteTrackFormat(this.props.track, this.props.params.format)

      this._onClose()
    }
  }

  _getMessage() {
    if (this._hasOnlyOneFormat()) {
      return 'This track only has one format. Deleting it will remove this track from your DISCO. This can’t be undone.'
    } else {
      return (
        <span>
          Are you sure you want to delete{' '}
          <strong>.{this.props.params.format}</strong> version of{' '}
          <strong>“{this.props.track.name}”</strong>? This can’t be undone.
        </span>
      )
    }
  }

  render() {
    return (
      <div>
        <DocumentHeadTags title="Delete track format | DISCO" />
        <ModalDialog
          title="Delete track format"
          primaryAction={{
            caption: 'Delete',
            onClick: this._onDelete,
          }}
          status="danger"
          onCancel={this._onClose}
        >
          {this._getMessage()}
        </ModalDialog>
      </div>
    )
  }
}

const ConnectedDeleteTrackFormat = connector(DeleteTrackFormat)

// Wrapper component to provide router params and navigation to the connected component
const DeleteTrackFormatWithRouter = (props: any) => {
  const params = useParams<{ trackId: string; format: string }>()
  const navigate = useNavigate()

  return (
    <ConnectedDeleteTrackFormat
      {...props}
      params={params}
      navigate={navigate}
    />
  )
}

export default DeleteTrackFormatWithRouter
